/**
 * TodoCheckBox.tsx — 일일 업무 카드의 「결과 확인」 단추줄 (PC · 2026-10-22 지도사)
 *  「직원이 물어만 보고 완료 처리 · 팀장님께 안내까지 해야 완료」 · 「심플하고 직관적이어야」
 *  직원: 답 기다려요 · 답 적기 → (지도사 확인) → 안내했어요
 *  지도사: 직원이 안내 · 내가 안내 · 다시 봐 주세요 → 안내했어요
 *  안내받을 분이 없는 일(「알아보고 나에게 결과 알려 줘」 · 2026-10-22)은 「확인했어요」로 바로 끝
 *  화면마다 단추 두세 개 · 읽고 이해할 칸은 두지 않는다. 규칙은 서버(services/todoCheck.js)가 지킨다.
 */
import { useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { C, T, R, IMEArea, IMEText } from './ui';
import * as todosApi from '../api-todos';
import type { Todo } from '../types';
import type { Staff } from './AssigneeChipPicker';
import { RESULT_MIN, copyText, kstTodayStr, mdOf, realLen, stageOf, staffNames, waitChoices, waitingNote } from '../todoCheckView';

interface Props {
  todo: Todo;
  staff: Staff[];
  myId: string;
  onChange: (t: Todo) => void;
  onToast?: (msg: string, kind?: 'success' | 'error') => void;
}

const btn = (primary?: boolean): CSSProperties => ({
  padding: '7px 14px', borderRadius: R.sm, fontSize: T.sm, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
  border: primary ? 'none' : `1px solid ${C.borderDk}`, background: primary ? C.text : C.white, color: primary ? C.white : C.text,
});
const link: CSSProperties = { border: 'none', background: 'none', padding: '4px 2px', fontSize: T.xs, color: C.sub, cursor: 'pointer', textDecoration: 'underline' };
const box: CSSProperties = { marginTop: 8, padding: '9px 11px', background: C.bgAlt, border: `1px solid ${C.border}`, borderRadius: R.sm, fontSize: T.sm, color: C.text, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'keep-all', overflowWrap: 'anywhere' };

export default function TodoCheckBox({ todo, staff, myId, onChange, onToast }: Props) {
  const stage = stageOf(todo, myId);
  const [mode, setMode] = useState<'' | 'answer' | 'wait' | 'back' | 'end' | 'notify'>('');
  const [note, setNote] = useState('');
  const [who, setWho] = useState(todo.notify_to || '');
  const [tel, setTel] = useState(todo.notify_phone || '');
  const [reason, setReason] = useState('');
  const [picks, setPicks] = useState<todosApi.ContactPick[]>([]);
  const [busy, setBusy] = useState(false);
  const name = staffNames(todo, staff);

  useEffect(() => { setMode(''); setWho(todo.notify_to || ''); setTel(todo.notify_phone || ''); }, [todo.id, todo.status]);
  // 답 적기를 열면 FP 관리에서 같은 이름 후보를 불러 둔다(연락처가 비었을 때만 · 동명이인은 소속을 보고 고른다)
  useEffect(() => {
    if (mode !== 'answer' || tel || !who) { setPicks([]); return; }
    let live = true;
    todosApi.findContacts(todo.id, who).then(r => { if (live && r.success && r.data) setPicks(r.data); }).catch(() => undefined);
    return () => { live = false; };
  }, [mode, who, tel, todo.id]);

  async function run(p: Promise<{ success: boolean; data?: Todo; error?: string }>, okMsg: string) {
    if (busy) return;
    setBusy(true);
    try {
      const r = await p;
      if (r.success && r.data) { onChange({ ...todo, ...r.data }); onToast?.(okMsg, 'success'); setMode(''); setNote(''); setReason(''); }
      else { if (r.data) onChange({ ...todo, ...r.data }); onToast?.(r.error || '저장하지 못했어요', 'error'); }   // 답이 그사이 고쳐졌으면 새 답으로 바꿔 보여 준다
    } catch (e) { onToast?.(e instanceof Error ? e.message : '저장하지 못했어요', 'error'); }
    finally { setBusy(false); }
  }
  const copy = async () => { onToast?.((await copyText(todo.result_note || '')) ? '답을 복사했어요' : '복사하지 못했어요', 'success'); };
  const contactLine = (todo.notify_to || todo.notify_phone)
    ? <div style={{ fontSize: T.sm, color: C.text, fontWeight: 600 }}>{todo.notify_to || '안내받을 분'}{todo.notify_phone ? <span style={{ color: C.sub, fontWeight: 400 }}> · {todo.notify_phone}</span> : <span style={{ color: C.muted, fontWeight: 400 }}> · 연락처 없음</span>}</div>
    : null;
  const answer = todo.result_note ? <div style={box}>{todo.result_note}</div> : null;
  const row = (children: ReactNode) => <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>{children}</div>;

  // 답 적기 칸 — 처음 적을 때와 보낸 뒤 고칠 때 같이 쓴다(2026-10-22 「직원이 답변을 수정할 수도」)
  //  확인 전에 고치면 그대로 확인 기다림 · 지도사 확인 뒤(안내할 차례)에 고치면 다시 확인 기다림으로
  const editing = stage === 'waitCheck' || stage === 'staffNotify';
  const openAnswer = () => { setNote(todo.result_note || ''); setMode('answer'); };
  const answerPanel = (
      <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
        <IMEArea value={note} onChange={setNote} rows={4} autoFocus
          placeholder={who.trim() ? '팀장님께 전할 답 (알아본 내용과 앞으로 어떻게 할지)' : '알아본 결과 (지도사님께 알릴 내용)'}
          style={{ width: '100%', padding: '9px 11px', border: `1px solid ${C.borderDk}`, borderRadius: R.sm, fontSize: T.md, lineHeight: 1.6, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 6 }}>
          <IMEText value={who} onChange={setWho} placeholder="안내받을 분 (없으면 비워 두기)"
            style={{ padding: '7px 10px', border: `1px solid ${C.border}`, borderRadius: R.sm, fontSize: T.sm, minWidth: 0 }} />
          <input value={tel} onChange={e => setTel(e.target.value)} placeholder="연락처 (모르면 비워 두기)" inputMode="tel"
            style={{ padding: '7px 10px', border: `1px solid ${C.border}`, borderRadius: R.sm, fontSize: T.sm, minWidth: 0 }} />
        </div>
        {picks.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {picks.filter(p => p.phone).map(p => (
              <button key={p.id} style={{ ...btn(), fontWeight: 400, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                onClick={() => { setTel(p.phone); if (!who.trim()) setWho(`${p.name} 팀장님`); }}>
                {p.name}{p.company ? ` · ${p.company}` : ''} · {p.phone}
              </button>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button style={{ ...btn(true), opacity: realLen(note) >= RESULT_MIN ? 1 : 0.4 }} disabled={busy || realLen(note) < RESULT_MIN}
            onClick={() => void run(todosApi.writeResult(todo.id, { note, notify_to: who, notify_phone: tel }), editing ? '고친 답을 보냈어요 · 지도사님이 다시 확인해요' : '지도사님께 보냈어요')}>{editing ? '고친 답 보내기' : '보내기'}</button>
          <button style={link} onClick={() => setMode('')}>그만두기</button>
          {realLen(note) > 0 && realLen(note) < RESULT_MIN && <span style={{ fontSize: T.xs, color: C.muted }}>조금만 더 적어 주세요</span>}
        </div>
      </div>
  );

  /* ── 직원: 답 기다려요 · 답 적기 ── */
  if (stage === 'write') {
    const waiting = waitingNote(todo);
    // 지도사가 돌려보낸 일 = 답을 한 번 보낸 적이 있는데 다시 진행 중
    const back = todo.result_at ? `다시 봐 주세요${todo.check_note ? ' · ' + todo.check_note : ''}` : '';
    return (
      <div>
        {back && <div style={{ ...box, background: C.orangeLt, borderColor: '#FED7AA' }}>{back}</div>}
        {waiting && mode === '' && <div style={{ marginTop: 8, fontSize: T.sm, color: C.sub }}>{waiting}</div>}
        {mode === '' && row(<>
          <button style={btn()} disabled={busy} onClick={() => setMode('wait')}>답 기다려요</button>
          <button style={btn(true)} disabled={busy} onClick={openAnswer}>답 적기</button>
        </>)}
        {mode === 'wait' && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: T.xs, color: C.sub, marginBottom: 6 }}>언제 다시 볼까요?</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {waitChoices(kstTodayStr()).map(w => (
                <button key={w.label} style={btn()} disabled={busy}
                  onClick={() => void run(todosApi.waitReply(todo.id, { where: '', until: w.date }), `${mdOf(w.date)}에 다시 올라와요`)}>
                  {w.label} <span style={{ color: C.muted, fontWeight: 400 }}>{mdOf(w.date)}</span>
                </button>
              ))}
              <button style={link} onClick={() => setMode('')}>그만두기</button>
            </div>
          </div>
        )}
        {mode === 'answer' && answerPanel}
      </div>
    );
  }

  /* ── 직원: 확인 기다림 · 안내할 차례 · 지도사가 안내 ── */
  if (stage === 'waitCheck') return mode === 'answer' ? answerPanel : (
    <div>{answer}
      <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center' }}>
        <span style={{ fontSize: T.sm, color: C.sub }}>지도사님 확인 기다리는 중</span>
        <button style={link} onClick={openAnswer}>답 고치기</button>
      </div>
    </div>
  );
  if (stage === 'bossNotifies') return <div>{answer}<div style={{ marginTop: 6, fontSize: T.sm, color: C.sub }}>지도사님이 직접 안내해요</div></div>;
  // 무엇을 하면 되는지 한 줄로(2026-10-22 지도사 「오재인 화면에는 뭐가 나와」 · 이름 · 번호만으로는 할 일이 안 읽힘)
  if (stage === 'staffNotify') return (
    <div>
      <div style={{ marginTop: 8, fontSize: T.sm, fontWeight: 700, color: C.accent }}>지도사님이 확인했어요 · {todo.notify_to || '안내받을 분'}께 아래 답을 안내해 주세요</div>
      <div style={{ marginTop: 4 }}>{contactLine}</div>
      {answer}
      {todo.check_note && <div style={{ marginTop: 6, fontSize: T.sm, color: C.text }}>지도사님 · {todo.check_note}</div>}
      {mode === 'answer' ? answerPanel : row(<>
        <button style={btn()} onClick={() => void copy()}>답 복사</button>
        <button style={btn(true)} disabled={busy} onClick={() => void run(todosApi.markNotified(todo.id), '끝났어요')}>안내했어요</button>
        <button style={link} onClick={openAnswer} title="고치면 지도사님이 다시 확인해요">답 고치기</button>
      </>)}
    </div>
  );

  /* ── 지도사: 알아보는 중 · 확인할 차례 · 안내할 차례 ── */
  if (stage === 'waitResult') {
    const waiting = waitingNote(todo);
    return (
      <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: T.sm, color: C.sub }}>{waiting ? `${name} · ${waiting}` : `${name} 님이 알아보는 중`}</span>
        {mode === 'end'
          ? <><button style={btn()} disabled={busy} onClick={() => void run(todosApi.updateTodo(todo.id, { status: 'done', progress: 100 }), '끝냈어요')}>결과 없이 끝내기</button><button style={link} onClick={() => setMode('')}>그만두기</button></>
          : <button style={link} onClick={() => setMode('end')}>그냥 끝내기</button>}
      </div>
    );
  }
  if (stage === 'check') return (
    <div>
      {answer}
      <div style={{ marginTop: 6 }}>{contactLine}</div>
      {mode === 'back' ? (
        <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
          <IMEText value={reason} onChange={setReason} placeholder="무엇을 더 봐야 하나요 (안 적어도 돼요)" autoFocus
            style={{ padding: '7px 10px', border: `1px solid ${C.border}`, borderRadius: R.sm, fontSize: T.sm }} />
          <div style={{ display: 'flex', gap: 6 }}>
            <button style={btn(true)} disabled={busy} onClick={() => void run(todosApi.checkTodo(todo.id, { action: 'back', reason, seen: todo.result_at }), `${name} 님께 돌려보냈어요`)}>돌려보내기</button>
            <button style={link} onClick={() => setMode('')}>그만두기</button>
          </div>
        </div>
      ) : (todo.notify_to || mode === 'notify') ? row(<>
        <button style={btn(true)} disabled={busy} onClick={() => void run(todosApi.checkTodo(todo.id, { action: 'ok', notify_by: 'staff', seen: todo.result_at }), `${name} 님이 안내해요`)}>{name} 님이 안내</button>
        <button style={btn(true)} disabled={busy} onClick={() => void run(todosApi.checkTodo(todo.id, { action: 'ok', notify_by: 'me', seen: todo.result_at }), '내가 안내할 일로 옮겼어요')}>내가 안내</button>
        <button style={link} onClick={() => setMode('back')}>다시 봐 주세요</button>
        <button style={link} disabled={busy} onClick={() => void run(todosApi.checkTodo(todo.id, { action: 'done', seen: todo.result_at }), '확인했어요')}>안내 없이 끝내기</button>
      </>) : row(<>
        <button style={btn(true)} disabled={busy} onClick={() => void run(todosApi.checkTodo(todo.id, { action: 'done', seen: todo.result_at }), '확인했어요')}>확인했어요</button>
        <button style={link} onClick={() => setMode('back')}>다시 봐 주세요</button>
        <button style={link} onClick={() => setMode('notify')}>팀장님께 안내도 해야 해요</button>
      </>)}
    </div>
  );
  if (stage === 'bossNotify') return (
    <div>
      <div style={{ marginTop: 8 }}>{contactLine}</div>
      {answer}
      {row(<>
        <button style={btn()} onClick={() => void copy()}>답 복사</button>
        <button style={btn(true)} disabled={busy} onClick={() => void run(todosApi.markNotified(todo.id), '끝났어요')}>안내했어요</button>
      </>)}
    </div>
  );
  // 「오재인 님이 안내하는 중」만으로는 누구에게 · 무엇을 · 어떻게 전하는지 안 읽힘(2026-10-27 지도사 「정말 오재인이 안내를 하는 건지 어떻게 안내를 하겠다는 건지」)
  //  → 누구에게(이름 · 번호) · 무엇을(아래 답 그대로) · 끝나는 때(오재인이 「안내했어요」) 한 줄 + 내가 대신 전했으면 끝낼 수 있게
  if (stage === 'staffNotifying') return (
    <div>
      <div style={{ marginTop: 8 }}>{contactLine}</div>
      {answer}
      <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: T.sm, color: C.sub }}>{name} 님이 위 답을 전할 차례 · 전하고 「안내했어요」를 누르면 끝나요</span>
        {mode === 'end'
          ? <><button style={btn()} disabled={busy} onClick={() => void run(todosApi.markNotified(todo.id), '끝났어요')}>내가 안내했어요</button><button style={link} onClick={() => setMode('')}>그만두기</button></>
          : <button style={link} onClick={() => setMode('end')}>내가 대신 안내했어요</button>}
      </div>
    </div>
  );
  if (stage === 'done' && todo.result_note) return <div style={{ ...box, color: C.sub }}>{todo.result_note}</div>;
  return null;
}
